import { useMemo, useState } from 'react';
import { X, User, Calendar, Tag, FileText, Bot, MessageSquare, CheckCircle2, Clock, Copy, Download, Code2 } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import type { LogRecord } from './types';
import { downloadText, formatLogDate } from './logUtils';

interface LogDetailsModalProps {
  data: LogRecord | null;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  tipoSolicitacao: 'Tipo de solicitação',
  idIFC: 'ID Infracommerce',
  tipoMecanica: 'Tipo de mecânica',
  tipoLista: 'Tipo de lista',
  idListaOriginal: 'ID lista original',
  recompraDisponivel: 'Recompra disponível',
  nomePromo: 'Nome interno da promoção',
  nomeExibicao: 'Nome de exibição',
  descricao: 'Descrição da promoção',
  descricaoInterna: 'Descrição interna',
  industria: 'Indústria',
  multiPatrocinador: 'Multi patrocinador',
  patrocinadores: 'Patrocinadores',
  cumulativa: 'Cumulativa',
  inicioVigencia: 'Início da vigência',
  fimVigencia: 'Fim da vigência',
  budgetAtrelado: 'Budget atrelado',
  budgetValor: 'Valor do budget',
  sellersIncluidos: 'Sellers incluídos',
  sellersExcecao: 'Sellers exceção',
  cnpjsIncluidos: 'CNPJs incluídos',
  cnpjsExcecao: 'CNPJs exceção',
  canais: 'Canais',
  tipoPedido: 'Tipo de pedido',
  tipoDesconto: 'Tipo de desconto',
  valorDesconto: 'Valor do desconto',
  criterioDesconto: 'Critério de desconto',
  qtdMinima: 'Quantidade mínima',
  qtdMaxima: 'Quantidade máxima',
  valorMinimo: 'Valor mínimo',
  valorMaximo: 'Valor máximo',
  numeroCard: 'Número do card',
  qtdCardsFilhos: 'Quantidade de cards filhos',
  tipoItemIncluido: 'Tipo de item incluído',
  itensIncluidos: 'Itens incluídos',
  tipoItemExcluido: 'Tipo de item excluído',
  itensExcluidos: 'Itens excluídos',
  eansObrigatorios: 'EANs obrigatórios',
  skusObrigatorios: 'SKUs obrigatórios',
  skuCombo: 'SKU é combo',
  tipoFamilia: 'Tipo de família',
  nomeFamilia: 'Nome da família',
  industriaFamilia: 'Indústria da família',
  obsFamilia: 'Observação da família',
  skusFamilia: 'SKUs da família',
  qtdEANsDistintos: 'Quantidade de EANs distintos',
  qtdMinimaAtivacao: 'Quantidade mínima para ativação',
  qtdMaximaAtivacao: 'Quantidade máxima para ativação',
  qtdFamiliasAtivacao: 'Quantidade de famílias para ativação',
  familiaObrigatoria: 'Família obrigatória',
  posicaoFamilia: 'Posição da família',
  descontoFamilia: 'Desconto da família',
  posicaoLista: 'Posição na lista',
  secaoLista: 'Seção da lista',
  cupom: 'Cupom',
  codigoCupom: 'Código do cupom',
  qtdCupons: 'Quantidade de cupons',
  usosPorCliente: 'Usos por cliente',
};

const humanizeFieldKey = (key: string): string => {
  const childMatch = key.match(/(.+)__child(\d+)$/);
  const baseKey = childMatch ? childMatch[1] : key;
  const suffix = childMatch ? ` (Filho ${childMatch[2]})` : '';
  return (FIELD_LABELS[baseKey] || baseKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase())) + suffix;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

// Log data can be an object, a JSON primitive, or an unstructured legacy string.
const decodePayload = (data: LogRecord | null) => {
  const raw = data?.json ?? data?.data;
  if (raw === undefined || raw === null || raw === '') return { value: null, text: '', malformed: false };
  if (typeof raw !== 'string') return { value: raw, text: JSON.stringify(raw, null, 2), malformed: false };
  try {
    const value = JSON.parse(raw);
    return { value, text: JSON.stringify(value, null, 2), malformed: false };
  } catch {
    return { value: raw, text: raw, malformed: true };
  }
};

// Conversation timestamps are real instants; the log's created_at is already a wall-clock value.
const formatMessageDate = (value: string | number): string => {
  if (typeof value === 'string' && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return formatLogDate(value);
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).format(date).replace(',', '');
};

const normalizeMessages = (json: any): any[] => {
  const values = [json?.conversation?.messages, json?.conversation?.exchange, json?.conversation?.history].find(Array.isArray) || [];
  return values.filter((value: any) => value && typeof value === 'object');
};

const normalizeFilledValues = (json: any): Record<string, unknown> => {
  const value = json?.form?.filledValues ?? json?.form?.suggestedValues ?? json?.form?.latestSuggestedValues;
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};

const LogDetailsModal = ({ data, onClose }: LogDetailsModalProps) => {
  const [copyStatus, setCopyStatus] = useState('');
  const payload = useMemo(() => decodePayload(data), [data]);
  const recordJson = useMemo(() => data ? JSON.stringify({
    id_log: data.id_log,
    id_log_type: data.id_log_type,
    user_id: data.user_id,
    user: data.user,
    type: data.type,
    name: data.name,
    created_at: data.created_at,
    ...(data.groupedLogIds?.length ? { groupedLogIds: data.groupedLogIds } : {}),
    groupingTruncated: Boolean(data.groupingTruncated),
    data: payload.value,
  }, null, 2) : '', [data, payload]);
  if (!data) return null;

  const json: any = payload.value;
  const isAgentLog = data.type === 'briefing-promocoes-agent-chat' || json?.source === 'briefing-promocoes-agent';
  const messages = normalizeMessages(json);
  const filledEntries = Object.entries(normalizeFilledValues(json));
  const interactions: any[] = Array.isArray(json?.form?.interactions) ? json.form.interactions.filter((item: any) => item && typeof item === 'object') : [];
  const status = json?.status === 'error' ? 'Erro' : json?.status === 'missing-template' ? 'Sem template' : 'Concluído';
  const statusColor = json?.status === 'error' ? 'danger' : json?.status === 'missing-template' ? 'warning' : 'success';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recordJson);
      setCopyStatus('JSON copiado.');
    } catch {
      setCopyStatus('Não foi possível copiar. Você pode baixar o JSON ou selecionar o conteúdo abaixo.');
    }
  };

  const payloadContent = (
    <div className="logs-detail-payload">
      {payload.malformed && <div className="alert alert-warning py-3 mb-3" role="status">
        Este registro contém texto sem estrutura JSON válida. O conteúdo original está preservado abaixo.
      </div>}
      {payload.text ? <pre className="bg-light rounded p-4 mb-0" tabIndex={0} aria-label="Conteúdo do registro"
        style={{ maxHeight: 440, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>
        <code>{payload.text}</code>
      </pre> : <div className="bg-light rounded p-5 text-gray-600">Este registro não possui dados adicionais.</div>}
    </div>
  );

  return <>
    <Modal.Header className="pb-4">
      <div className="flex-grow-1">
        <div className="text-primary fw-semibold fs-8 text-uppercase mb-2">Registro #{data.id_log}</div>
        <Modal.Title as="h2" id="log-details-title" className="fw-bold fs-3 mb-1">
          {isAgentLog ? 'Conversa do Agente de Briefing' : 'Detalhes da atividade'}
        </Modal.Title>
        <p className="text-gray-600 fs-7 mb-0" style={{ overflowWrap: 'anywhere' }}>{data.name || 'Atividade sem descrição'}</p>
      </div>
      <button type="button" className="btn btn-sm btn-icon btn-light ms-3 flex-shrink-0" onClick={onClose} aria-label="Fechar detalhes">
        <X size={20} aria-hidden="true" />
      </button>
    </Modal.Header>

    <Modal.Body className="pt-5">
      <div className="row g-3 mb-5 logs-detail-metadata">
        {[
          { label: 'Usuário', value: data.user || 'Sistema / não identificado', Icon: User },
          { label: 'Tipo de atividade', value: data.type || 'Sem tipo', Icon: Tag },
          { label: 'Data e hora', value: formatLogDate(data.created_at), Icon: Calendar },
        ].map(({ label, value, Icon }) => <div className="col-md-4" key={label}>
          <div className="bg-light rounded p-4 h-100 d-flex align-items-start gap-3">
            <Icon size={17} className="text-primary mt-1 flex-shrink-0" aria-hidden="true" />
            <div style={{ minWidth: 0 }}>
              <div className="text-gray-600 fs-8 mb-1">{label}</div>
              <div className="text-gray-900 fw-semibold fs-7" style={{ overflowWrap: 'anywhere' }}>{value}</div>
            </div>
          </div>
        </div>)}
      </div>

      {isAgentLog && <>
        {data.groupingTruncated && <div className="alert alert-warning py-3 fs-7" role="status">
          Esta conversa é extensa. A visualização reúne os registros mais recentes e o registro selecionado; interações mais antigas podem não estar incluídas.
        </div>}
        {data.groupedLogIds && data.groupedLogIds.length > 1 && <div className="alert alert-primary py-3 fs-7">
          Conversa consolidada a partir de {data.groupedLogIds.length} registros.
        </div>}
        <div className="row g-3 mb-5">
          {[
            { label: 'Template', value: formatValue(json?.templateName ?? 'Não informado'), Icon: Bot },
            { label: 'Mensagens', value: messages.length, Icon: MessageSquare },
            { label: 'Campos preenchidos', value: filledEntries.length, Icon: CheckCircle2 },
            { label: 'Status', value: status, Icon: Clock },
          ].map(({ label, value, Icon }) => <div className="col-6 col-lg-3" key={label}>
            <div className="border border-gray-300 rounded p-4 h-100">
              <Icon size={18} className={`text-${label === 'Status' ? statusColor : 'primary'} mb-3`} aria-hidden="true" />
              <div className="text-gray-600 fs-8 mb-1">{label}</div>
              <div className="text-gray-900 fw-bold fs-7" style={{ overflowWrap: 'anywhere' }}>{value}</div>
            </div>
          </div>)}
        </div>
        <div className="row g-5 mb-5">
          <div className="col-lg-7">
            <section className="border border-gray-300 rounded p-4 h-100" aria-labelledby="log-conversation-title">
              <h3 id="log-conversation-title" className="fw-bold fs-5 mb-4">Conversa</h3>
              <div className="d-flex flex-column gap-3" style={{ maxHeight: 560, overflowY: 'auto' }} tabIndex={0} aria-label="Mensagens da conversa">
                {!messages.length ? <p className="text-gray-600">Nenhuma mensagem registrada.</p> : messages.map((message, index) => {
                  const isUser = message.role === 'user';
                  return <div key={index} className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
                    <div className={`rounded p-4 ${isUser ? 'bg-primary text-white' : 'bg-light-primary text-gray-900'}`}
                      style={{ maxWidth: '90%', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      <div className={`fs-8 fw-bold mb-2 ${isUser ? 'text-white' : 'text-primary'}`}>{isUser ? 'Usuário' : 'Agente'}</div>
                      <div className="fs-7">{formatValue(message.content)}</div>
                      {message.timestamp && <div className={`fs-8 mt-2 ${isUser ? 'text-white' : 'text-gray-600'}`}>{formatMessageDate(message.timestamp)}</div>}
                    </div>
                  </div>;
                })}
              </div>
            </section>
          </div>
          <div className="col-lg-5">
            <section className="border border-gray-300 rounded p-4 mb-4" aria-labelledby="log-fields-title">
              <h3 id="log-fields-title" className="fw-bold fs-5 mb-4">Campos preenchidos pelo agente</h3>
              {!filledEntries.length ? <p className="text-gray-600 mb-0">Nenhum campo preenchido automaticamente.</p> :
                <dl className="d-flex flex-column gap-3 mb-0" style={{ maxHeight: 330, overflowY: 'auto' }} tabIndex={0}>
                  {filledEntries.map(([key, value]) => <div key={key} className="border-bottom border-gray-200 pb-3">
                    <dt className="text-gray-700 fs-8 fw-bold mb-1">{humanizeFieldKey(key)}</dt>
                    <dd className="text-gray-900 fs-7 mb-0" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatValue(value)}</dd>
                  </div>)}
                </dl>}
            </section>
            <section className="border border-gray-300 rounded p-4" aria-labelledby="log-interactions-title">
              <h3 id="log-interactions-title" className="fw-bold fs-5 mb-4">Interações</h3>
              {!interactions.length ? <p className="text-gray-600 mb-0">Sem detalhes de interações.</p> :
                <div className="d-flex flex-column gap-3" style={{ maxHeight: 230, overflowY: 'auto' }} tabIndex={0}>
                  {interactions.map((interaction, index) => {
                    const fields: string[] = Array.isArray(interaction.suggestedFieldKeys) ? interaction.suggestedFieldKeys.filter((field: unknown) => typeof field === 'string') : [];
                    return <div key={index} className="bg-light rounded p-3">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <span className="fw-bold text-gray-800 fs-7">Troca {index + 1}</span>
                        <span className={`badge badge-light-${interaction.status === 'error' ? 'danger' : 'primary'}`}>
                          {interaction.status === 'error' ? 'Erro · ' : ''}{fields.length} campo(s)
                        </span>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {fields.map((field, fieldIndex) => <span key={fieldIndex} className="badge badge-light" style={{ whiteSpace: 'normal', textAlign: 'left' }}>{humanizeFieldKey(field)}</span>)}
                      </div>
                    </div>;
                  })}
                </div>}
            </section>
          </div>
        </div>
      </>}

      {isAgentLog ? <details className="border border-gray-300 rounded p-4">
        <summary className="fw-semibold text-gray-800" style={{ cursor: 'pointer' }}><Code2 size={16} className="me-2" aria-hidden="true" />Dados completos do registro</summary>
        <div className="mt-4">{payloadContent}</div>
      </details> : <section aria-labelledby="log-payload-title">
        <h3 id="log-payload-title" className="fs-5 fw-bold mb-4 d-flex align-items-center gap-2"><FileText size={18} aria-hidden="true" />Dados do registro</h3>
        {payloadContent}
      </section>}
    </Modal.Body>

    <Modal.Footer className="d-flex flex-wrap justify-content-between gap-3">
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-sm btn-light-primary d-flex align-items-center gap-2" onClick={handleCopy}>
          <Copy size={15} aria-hidden="true" />Copiar JSON
        </button>
        <button type="button" className="btn btn-sm btn-light-primary d-flex align-items-center gap-2"
          onClick={() => downloadText(recordJson, `log-${data.id_log}.json`, 'application/json;charset=utf-8')}>
          <Download size={15} aria-hidden="true" />Baixar JSON
        </button>
      </div>
      <button type="button" className="btn btn-sm btn-light" onClick={onClose}>Fechar</button>
      {copyStatus && <div className="w-100 text-gray-700 fs-7" role="status">{copyStatus}</div>}
    </Modal.Footer>
  </>;
};

export default LogDetailsModal;
